/**
 * APP_HEX — Raw Hex Values
 * ─────────────────────────────────────────────────────────────────────────────
 * Use ONLY for:
 *   • Chart color configs
 *   • Inline gradient strings
 *   • Glow / box-shadow inline styles
 *
 * For className strings, always use the Tailwind class-pair exports in
 * colors.constant.ts (APP_BG, APP_TEXT, APP_SEMANTIC, etc.)
 *
 * Colors are grouped by theme preset name (matching Profile color picker)
 * so every preset's gradient stops live together.
 */

export const APP_HEX = {

  // ══════════════════════════════════════════════════════════════════════════
  // THEME PRESETS — Solid + Gradient stops grouped by preset name
  // ══════════════════════════════════════════════════════════════════════════

  // ─── Cantaloupe (default brand) ───────────────────────────────────────────
  /** Solid preset primary stop (amber) */
  primary: '#f5740b',
  /** Brand secondary (red-orange) */
  secondary: '#f45708',
  /** Brand tertiary (magenta) */
  tertiary: '#dd3e26',

  // ─── 365rm ────────────────────────────────────────────────────────────────
  /** Solid preset + gradient primary stop (brand blue) */
  rm365: '#006dff',
  /** Brand secondary (mid-blue) */
  rm365Secondary: '#2f89ff',
  /** Brand tertiary (light-blue) */
  rm365Tertiary: '#4a97fb',

  // ─── Emerald / Forest ─────────────────────────────────────────────────────
  /** Solid preset + Forest gradient primary (emerald) */
  success: '#10b981',
  successStrong: '#16a34a',
  /** Forest gradient mid (green) */
  green: '#22c55e',
  /** Forest gradient end (lime) */
  lime: '#84cc16',

  // ─── Rose ─────────────────────────────────────────────────────────────────
  /** Solid preset (rose) */
  rose: '#f43f5e',

  // ─── Violet / Royal ───────────────────────────────────────────────────────
  /** Solid preset + Royal gradient primary (violet) */
  purple: '#7d3af0',
  /** Royal gradient mid */
  violet: '#9158f3',
  /** Royal gradient end */
  indigo: '#a376f1',

  // ─── Slate / Steel ────────────────────────────────────────────────────────
  /** Steel gradient primary */
  slate: '#475569', // slate-600
  /** Solid preset + Steel gradient mid */
  slateMid: '#64748b', // slate-500
  /** Steel gradient end */
  slateDark: '#6d7580', // slate-700

  // ─── Ocean ────────────────────────────────────────────────────────────────
  /** Ocean gradient primary */
  sky: '#0ea5e9',
  /** Ocean gradient mid */
  cyan: '#24bad5',
  /** Ocean gradient end */
  teal: '#37d3ef',

  // ══════════════════════════════════════════════════════════════════════════
  // UTILITY — Chart fills, semantic indicators, UI chrome
  // ══════════════════════════════════════════════════════════════════════════

  // ─── Additional base palette (charts, badges) ─────────────────────────────
  blue: '#3b82f6',
  pink: '#ec4899',
  fuchsia: '#d946ef',

  // ─── Soft / Dark tint pairs (donut & bar charts) ──────────────────────────
  yellowSoft: '#fcd34d',
  cyanSoft: '#22d3ee',
  blueSoft: '#60a5fa',
  violetSoft: '#a78bfa',
  emeraldSoft: '#34d399', // emerald-400
  emeraldLight: '#6ee7b7', // emerald-300
  emeraldDark: '#059669',
  amberSoft: '#fbbf24',
  amberDark: '#d97706',
  redSoft: '#f87171',
  redDark: '#dc2626',
  skyLight: '#38bdf8', // sky-400
  indigoSoft: '#818cf8', // indigo-400
  indigoLight: '#a5b4fc', // indigo-300
  violetMid: '#8b5cf6', // violet-500
  violetLight: '#c084fc', // violet-400

  // ─── Gray scale ───────────────────────────────────────────────────────────
  slate700: '#374151',
  slate400: '#94a3b8',
  gray400: '#9ca3af',
  gray200: '#e5e7eb',
  white: '#ffffff',

  // ─── Semantic indicators ──────────────────────────────────────────────────
  danger: '#ef4444',
  warning: '#f59e0b',
  orange: '#f97316',
  info: '#6366f1',
  fatal: '#7f1d1d',
  muted: '#6b7280',
  tooltip: '#111827',

  // ─── Skeleton shimmer ─────────────────────────────────────────────────────
  skeletonBar: '#e9ecf0',
  skeletonPill: '#e2e5ea',
  skeletonCircle: '#dde1e7',
  skeletonDarkBar: '#1a2233',
  skeletonDarkPill: '#1e293b',
  skeletonDarkCircle: '#1f2b3e',

  // ─── Platform icons ───────────────────────────────────────────────────────
  androidGreen: '#3ddc84', // Android robot brand green

} as const;
